import { daysAt, rangeIndexForTrade } from './ranges';

const norm = s =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const hasResolvesOn = preset =>
  !!(preset?.resolvesOn?.scopeIds?.length || preset?.resolvesOn?.answers?.length);

/** Ladder weight — higher rung is heavier. Falls back to array index + 1. */
const presetRung = (preset, index = 0) => {
  const raw = preset?.rung ?? preset?.position;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return index + 1;
};

const presetMatchesPicks = (p, { scopeIds, items, opts, modAns }) => {
  if (hasResolvesOn(p)) {
    const okS = (p.resolvesOn.scopeIds || []).some(id => scopeIds.has(String(id)));
    const okA = (p.resolvesOn.answers || []).some(a => {
      const have = (modAns[a.modifierId]?.selected_options || []).map(norm);
      return (a.options || []).some(o => have.includes(norm(o)));
    });
    return okS || okA;
  }
  if (!p.requires) return false;
  const okI = (p.requires.anyItems || []).some(x => items.includes(norm(x)));
  const okO = (p.requires.anyOptions || []).some(x => opts.includes(norm(x)));
  return okI || okO;
};

/**
 * A9 — heaviest preset (by rung) any of whose resolves_on / requires are present
 * in the picks. Nothing matching → lightest.
 */
export const resolvePreset = (cfg, picks) => {
  if (!cfg?.presets?.length) return null;
  const scopeIds = new Set((picks?.scopeIds || []).map(String));
  const items = (picks?.items || []).map(norm);
  const opts = (picks?.options || []).map(norm);
  const modAns = picks?.modifierAnswers || {};
  const ctx = { scopeIds, items, opts, modAns };

  // Sort lightest → heaviest so default and comparisons are order-independent.
  const ladder = [...cfg.presets]
    .map((p, i) => ({ p, i, rung: presetRung(p, i) }))
    .sort((a, b) => a.rung - b.rung || a.i - b.i);

  let hit = ladder[0].p;
  let hitRung = ladder[0].rung;

  ladder.forEach(({ p, rung }) => {
    if (!presetMatchesPicks(p, ctx)) return;
    if (rung > hitRung) {
      hit = p;
      hitRung = rung;
    }
  });
  return hit;
};

/** B3 — controlled items/answers/elements match the preset exactly. */
export const exactMatch = (cfg, preset, picks) => {
  if (!cfg || !preset) return false;

  // API 9 — match on scope ids + elements + modifier answers.
  if (preset.scopeIds) {
    const controlled = new Set();
    cfg.presets.forEach(p => (p.scopeIds || []).forEach(id => controlled.add(String(id))));
    const want = new Set((preset.scopeIds || []).map(String));
    const have = (picks?.scopeIds || []).map(String).filter(id => controlled.has(id));
    if (have.length !== want.size || have.some(id => !want.has(id))) return false;

    if (!scopesAndElementsMatch(cfg, preset, picks)) return false;
    return answersMatchExact(cfg, preset, picks);
  }

  // Fixtures — match on names / option strings.
  const controlled = new Set();
  cfg.presets.forEach(p => (p.items || []).forEach(x => controlled.add(norm(x))));
  const want = new Set((preset.items || []).map(norm));
  const have = (picks?.items || []).map(norm).filter(x => controlled.has(x));
  if (have.length !== want.size || have.some(x => !want.has(x))) return false;
  const opts = (picks?.options || []).map(norm);
  const ansOk = Object.keys(preset.answers || {}).every(k =>
    []
      .concat(preset.answers[k])
      .every(v => opts.includes(norm(v)))
  );
  const taOk = Object.keys(preset.tradeAnswers || {}).every(k =>
    opts.includes(norm(preset.tradeAnswers[k]))
  );
  return ansOk && taOk;
};

/** B7 — additions present that put the customer on this card. */
export const whyOnCard = (preset, picks) => {
  if (hasResolvesOn(preset)) {
    const scopeIds = new Set((picks?.scopeIds || []).map(String));
    const modAns = picks?.modifierAnswers || {};
    const fromScopes = (preset.resolvesOn.scopeIds || []).filter(id => scopeIds.has(String(id)));
    const fromOpts = (preset.resolvesOn.answers || []).flatMap(a => {
      const have = (modAns[a.modifierId]?.selected_options || []).map(norm);
      return (a.options || []).filter(o => have.includes(norm(o)));
    });
    // Prefer human labels from addsItems when available for scopes.
    const nameById = {};
    (preset.items || []).forEach((name, i) => {
      const id = preset.scopeIds?.[i];
      if (id) nameById[String(id)] = name;
    });
    return fromScopes.map(id => nameById[id] || id).concat(fromOpts);
  }
  if (!preset?.requires) return [];
  const items = (picks?.items || []).map(norm);
  const opts = (picks?.options || []).map(norm);
  return (preset.requires.anyItems || [])
    .filter(x => items.includes(norm(x)))
    .concat((preset.requires.anyOptions || []).filter(x => opts.includes(norm(x))));
};

export const cardById = (cfg, cardId) => (cfg?.cards || []).find(c => c.id === cardId) || null;

/** Lightest preset on the ladder (by rung / position, then catalog order). */
export const lightestPreset = cfg => {
  if (!cfg?.presets?.length) return null;
  return [...cfg.presets]
    .map((p, i) => ({ p, i, rung: presetRung(p, i) }))
    .sort((a, b) => a.rung - b.rung || a.i - b.i)[0].p;
};

/**
 * Preset marked Selected — only an explicit preset click (`chosenPresetId`).
 * Scope ticks never select; they only suggest (see suggestedPresetForUi).
 */
export const activePresetForUi = (cfg, picks, chosenPresetId, resolvedPreset = null) => {
  if (!cfg?.presets?.length || !chosenPresetId) return null;
  const chosen = cfg.presets.find(p => String(p.id) === String(chosenPresetId));
  if (!chosen) return null;
  if (exactMatch(cfg, chosen, picks)) return chosen;
  const suggested = matchPresetFromPicks(cfg, picks, { chosenPresetId });
  if (suggested && String(suggested.id) === String(chosen.id)) return chosen;
  // Still Selected (customised) only when there are picks and TAT still maps to this preset.
  const hasPicks = (picks?.scopeIds || []).length > 0;
  if (hasPicks && resolvedPreset && String(resolvedPreset.id) === String(chosen.id)) {
    return chosen;
  }
  return null;
};

/**
 * Preset inferred from picks (scopes + elements + answers). This is TAT-card applicable, not Selected.
 * Pass chosenPresetId so extras outside the selected preset can still resolve applicable.
 */
export const suggestedPresetForUi = (cfg, picks, chosenPresetId = null) => {
  if (!cfg?.presets?.length) return null;
  return matchPresetFromPicks(cfg, picks, { chosenPresetId });
};

const scopeIdSet = p => new Set((p?.scopeIds || []).map(String));

const sameScopeSet = (a, b) => {
  const as = scopeIdSet(a);
  const bs = scopeIdSet(b);
  if (as.size !== bs.size) return false;
  return [...as].every(id => bs.has(id));
};

const lastIsFullSuperset = ladder => {
  if (ladder.length < 2) return false;
  const last = ladder[ladder.length - 1];
  const lastIds = scopeIdSet(last);
  const earlier = ladder.slice(0, -1);
  const union = new Set();
  earlier.forEach(p => (p.scopeIds || []).forEach(id => union.add(String(id))));
  if (!union.size) return false;
  if (![...union].every(id => lastIds.has(id))) return false;
  // Same-scope answer variants: last is not bigger than each earlier package.
  return earlier.every(p => lastIds.size > scopeIdSet(p).size);
};

/**
 * Split ladder into mutually exclusive packages + optional full (last rung).
 * Last is full only when it is a real scope superset of the earlier packages.
 * Two cards that share the same scopes (and differ only by answers) are both packages.
 */
export const buildPresetPackages = cfg => {
  if (!cfg?.presets?.length) {
    return { ladder: [], packages: [], full: null, ownerPackage: new Map() };
  }
  const ladder = [...cfg.presets]
    .map((p, i) => ({ p, i, rung: presetRung(p, i) }))
    .sort((a, b) => a.rung - b.rung || a.i - b.i)
    .map(x => x.p);

  const full = lastIsFullSuperset(ladder) ? ladder[ladder.length - 1] : null;
  const packages = full ? ladder.slice(0, -1) : ladder;
  const ownerPackage = new Map();
  packages.forEach(p => {
    (p.scopeIds || []).forEach(id => {
      const key = String(id);
      if (!ownerPackage.has(key)) ownerPackage.set(key, p);
    });
  });
  return { ladder, packages, full, ownerPackage };
};

/** When two presets share scopes, keep the clicked one unless answers uniquely match the other. */
export const preferChosenWhenSameScopes = (cfg, chosen, suggested, picks) => {
  if (!suggested) return chosen || null;
  if (!chosen) return suggested;
  if (String(chosen.id) === String(suggested.id)) return chosen;
  if (!sameScopeSet(chosen, suggested)) return suggested;
  if (exactMatch(cfg, suggested, picks) && !exactMatch(cfg, chosen, picks)) return suggested;
  return chosen;
};

const scopeSet = ids => new Set((ids || []).map(String));

const lightestAmong = presets => {
  if (!presets?.length) return null;
  return [...presets]
    .map((p, i) => ({ p, i, rung: presetRung(p, i) }))
    .sort((a, b) => a.rung - b.rung || a.i - b.i)[0].p;
};

const hasFilledAnswer = ans =>
  !!(ans && ((ans.selected_options || []).length > 0 || String(ans.text_value || '').trim()));

const scopesAndElementsMatch = (cfg, preset, picks) => {
  if (!cfg || !preset?.scopeIds) return false;
  const controlled = new Set();
  cfg.presets.forEach(p => (p.scopeIds || []).forEach(id => controlled.add(String(id))));
  const want = new Set((preset.scopeIds || []).map(String));
  const have = (picks?.scopeIds || []).map(String).filter(id => controlled.has(id));
  if (have.length !== want.size || have.some(id => !want.has(id))) return false;

  const haveEls = new Set((picks?.elementIds || []).map(String));
  const controlledEls = new Set();
  cfg.presets.forEach(p => Object.keys(p.elementMap || {}).forEach(id => controlledEls.add(String(id))));
  const wantOnEls = new Set(
    Object.entries(preset.elementMap || {})
      .filter(([, isOn]) => !!isOn)
      .map(([elId]) => String(elId))
  );
  if ([...wantOnEls].some(id => !haveEls.has(id))) return false;
  if ([...haveEls].some(id => controlledEls.has(id) && !wantOnEls.has(id))) return false;
  return true;
};

const answersMatchExact = (cfg, preset, picks) => {
  const modAns = picks?.modifierAnswers || {};
  const controlledMods = new Set();
  (cfg?.presets || []).forEach(p =>
    Object.keys(p.answersByModifierId || {}).forEach(id => controlledMods.add(String(id)))
  );
  for (const modId of controlledMods) {
    const want = preset.answersByModifierId?.[modId];
    const have = modAns[modId] || modAns[String(modId)];
    if (hasFilledAnswer(want)) {
      if (!answerEquals(have, want)) return false;
    } else if (hasFilledAnswer(have)) {
      return false;
    }
  }
  return true;
};

const heaviestAmong = presets => {
  if (!presets?.length) return null;
  return [...presets]
    .map((p, i) => ({ p, i, rung: presetRung(p, i) }))
    .sort((a, b) => b.rung - a.rung || b.i - a.i)[0].p;
};

const answerEquals = (have, want) => {
  const haveOpts = (have?.selected_options || []).map(norm);
  const wantOpts = (want?.selected_options || []).map(norm);
  const haveText = norm(have?.text_value || '');
  const wantText = norm(want?.text_value || '');
  if (wantOpts.length !== haveOpts.length || wantOpts.some(o => !haveOpts.includes(o))) return false;
  return haveText === wantText;
};

const uniqueOwnersForScopes = (ownerPackage, selectedScopeIds) => {
  const owners = [];
  const ownerIds = new Set();
  scopeSet(selectedScopeIds).forEach(id => {
    const o = ownerPackage.get(id);
    if (o && !ownerIds.has(String(o.id))) {
      ownerIds.add(String(o.id));
      owners.push(o);
    }
  });
  return owners;
};

/**
 * Suggest from scopes only (package ownership / full). Used as fallback.
 */
export const matchPresetFromSelectedScopes = (cfg, selectedScopeIds) => {
  const { ladder, packages, full, ownerPackage } = buildPresetPackages(cfg);
  if (!packages.length && !full) return null;
  const selected = scopeSet(selectedScopeIds);
  if (!selected.size) return null;

  const exactScopeSets = ladder.filter(p => {
    const ids = (p.scopeIds || []).map(String);
    if (!ids.length || ids.length !== selected.size) return false;
    return ids.every(id => selected.has(id));
  });
  // Shared identical scope sets → lightest; climbing via elements/answers handled in matchPresetFromPicks.
  if (exactScopeSets.length) return lightestAmong(exactScopeSets);

  const owners = uniqueOwnersForScopes(ownerPackage, selectedScopeIds);

  if (owners.length > 1 && full) return full;
  if (owners.length === 1) {
    const owner = owners[0];
    const ownerScopeIds = new Set((owner.scopeIds || []).map(String));
    const hasExtra = [...selected].some(id => !ownerScopeIds.has(id));
    if (hasExtra && full) return full;
    return owner;
  }

  const containing = ladder.filter(p =>
    (p.scopeIds || []).some(id => selected.has(String(id)))
  );
  if (containing.length) return lightestAmong(containing);
  return null;
};

/**
 * Picks that are outside a baseline preset's scope_ids / on-elements / answers.
 */
const extrasBeyondPreset = (baseline, picks) => {
  if (!baseline) return { scopes: [], elementIds: [], answers: [] };
  const baseScopes = new Set((baseline.scopeIds || []).map(String));
  const scopes = (picks?.scopeIds || []).map(String).filter(id => !baseScopes.has(id));
  const elementIds = (picks?.elementIds || [])
    .map(String)
    .filter(id => baseline.elementMap?.[id] !== true);
  const answers = Object.entries(picks?.modifierAnswers || {}).filter(([modId, have]) => {
    const want = baseline.answersByModifierId?.[modId];
    if (!want) return true;
    return !answerEquals(have, want);
  });
  return { scopes, elementIds, answers };
};

const presetClaimsExtra = (preset, extras, picks) => {
  if (!preset) return false;
  if (extras.scopes.some(id => (preset.scopeIds || []).map(String).includes(id))) return true;
  if (extras.elementIds.some(id => preset.elementMap?.[id] === true)) return true;
  if (
    extras.answers.some(([modId, have]) => {
      const want = preset.answersByModifierId?.[modId];
      return !!(want && answerEquals(have, want));
    })
  ) {
    return true;
  }
  return false;
};

/**
 * Suggest a preset from full picks: scopes + elements + answers.
 * Extra picks outside the selected preset that exist on another preset → suggest that other.
 * Otherwise climb to heavier conflict-free claims; full when scopes span packages.
 */
export const matchPresetFromPicks = (cfg, picks, { chosenPresetId = null } = {}) => {
  if (!cfg?.presets?.length) return null;
  const { ladder, full, ownerPackage } = buildPresetPackages(cfg);
  const hasSignal =
    (picks?.scopeIds || []).length > 0 ||
    (picks?.elementIds || []).length > 0 ||
    Object.keys(picks?.modifierAnswers || {}).length > 0;
  if (!hasSignal) return null;

  const chosen =
    chosenPresetId != null && chosenPresetId !== ''
      ? cfg.presets.find(p => String(p.id) === String(chosenPresetId)) || null
      : null;

  const exact = ladder.filter(p => exactMatch(cfg, p, picks));
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) {
    const chosenExact = chosen && exact.find(p => String(p.id) === String(chosen.id));
    if (chosenExact) return chosenExact;
    return lightestAmong(exact);
  }

  // Same items, answers are the only difference — stay on the clicked preset
  // unless live answers uniquely match another card.
  if (chosen && scopesAndElementsMatch(cfg, chosen, picks)) {
    const answerHits = ladder.filter(
      p =>
        scopesAndElementsMatch(cfg, p, picks) &&
        Object.values(p.answersByModifierId || {}).some(hasFilledAnswer) &&
        answersMatchExact(cfg, p, picks)
    );
    if (answerHits.length === 1) return answerHits[0];
    return chosen;
  }

  // Selected preset + extras that live on another preset → suggest that other (heaviest).
  if (chosen) {
    const extras = extrasBeyondPreset(chosen, picks);
    const hasExtras =
      extras.scopes.length > 0 || extras.elementIds.length > 0 || extras.answers.length > 0;
    if (hasExtras) {
      const others = ladder.filter(
        p => String(p.id) !== String(chosen.id) && presetClaimsExtra(p, extras, picks)
      );
      if (others.length) return heaviestAmong(others);
    }
  }

  let best = null;
  let bestRung = -Infinity;
  const bump = p => {
    if (!p) return;
    const rung = presetRung(p);
    if (rung > bestRung) {
      best = p;
      bestRung = rung;
    }
  };

  // Scopes → package ownership (full when multiple packages or extra scopes beyond one package).
  const owners = uniqueOwnersForScopes(ownerPackage, picks?.scopeIds || []);
  if (owners.length > 1 && full) {
    bump(full);
  } else if (owners.length === 1) {
    bump(owners[0]);
    const ownerScopeIds = new Set((owners[0].scopeIds || []).map(String));
    const hasExtra = (picks?.scopeIds || []).some(id => !ownerScopeIds.has(String(id)));
    if (hasExtra && full) bump(full);
  } else if (full) {
    const fullIds = new Set((full.scopeIds || []).map(String));
    if (
      (picks?.scopeIds || []).some(
        id => fullIds.has(String(id)) && !ownerPackage.has(String(id))
      )
    ) {
      bump(full);
    }
  }

  // Element/answer climb: shared signals → lightest claimant; only promote heavier
  // when this pick is distinctive (not is_on/answer-matched on any lighter preset).
  // Do NOT drop the lighter preset just because other sibling elements conflict.
  (picks?.elementIds || []).forEach(elId => {
    const key = String(elId);
    const claimants = ladder.filter(p => p.elementMap && p.elementMap[key] === true);
    if (!claimants.length) return;
    const distinctive = claimants.filter(p => {
      const r = presetRung(p);
      return !ladder.some(
        lp => presetRung(lp) < r && lp.elementMap && lp.elementMap[key] === true
      );
    });
    bump(distinctive.length ? heaviestAmong(distinctive) : lightestAmong(claimants));
  });

  Object.entries(picks?.modifierAnswers || {}).forEach(([modId, have]) => {
    const claimants = ladder.filter(p => {
      const want = p.answersByModifierId?.[modId];
      return !!(want && answerEquals(have, want));
    });
    if (!claimants.length) return;
    const distinctive = claimants.filter(p => {
      const r = presetRung(p);
      return !ladder.some(lp => {
        if (presetRung(lp) >= r) return false;
        const want = lp.answersByModifierId?.[modId];
        return !!(want && answerEquals(have, want));
      });
    });
    bump(distinctive.length ? heaviestAmong(distinctive) : lightestAmong(claimants));
  });

  // Scope only on heavier rung — climb when no package owner claimed yet.
  if (!best && (picks?.scopeIds || []).length) {
    (picks.scopeIds || []).forEach(sid => {
      const claimants = ladder.filter(p =>
        (p.scopeIds || []).map(String).includes(String(sid))
      );
      if (!claimants.length) return;
      const distinctive = claimants.filter(p => {
        const r = presetRung(p);
        return !ladder.some(
          lp =>
            presetRung(lp) < r &&
            (lp.scopeIds || []).map(String).includes(String(sid))
        );
      });
      bump(distinctive.length ? heaviestAmong(distinctive) : lightestAmong(claimants));
    });
  }

  if (best) return preferChosenWhenSameScopes(cfg, chosen, best, picks);
  return preferChosenWhenSameScopes(
    cfg,
    chosen,
    matchPresetFromSelectedScopes(cfg, picks?.scopeIds || []),
    picks
  );
};

/**
 * Which preset a newly ticked scope suggests (same rules as match after toggle).
 */
export const resolvePresetOnScopeSelect = (cfg, selectedScopeIdsBefore, scopeId) => {
  const after = new Set(scopeSet(selectedScopeIdsBefore));
  after.add(String(scopeId));
  return matchPresetFromSelectedScopes(cfg, [...after]);
};

/**
 * TAT-card applicable preset after scope/element/answer picks.
 */
export const resolvePresetAfterPicks = (cfg, picks, chosenPresetId = null) => {
  if (!cfg?.presets?.length) return null;
  return matchPresetFromPicks(cfg, picks, { chosenPresetId });
};

/**
 * Preset linked to a TAT card for Switch:
 * prefer card.linkedPresetId (lightest on that card); else first ladder preset mapping to it.
 */
export const linkedPresetForCard = (cfg, cardId) => {
  if (!cfg?.presets?.length || !cardId) return null;
  const card = cardById(cfg, cardId);
  if (card?.linkedPresetId) {
    const linked = cfg.presets.find(p => p.id === card.linkedPresetId);
    if (linked) return linked;
  }
  return cfg.presets.find(p => p.cardId === cardId) || null;
};

export const resolveTradeTurnaround = (cfg, picks, { typicalStdIdx = null, iso, chosenPresetId = null } = {}) => {
  if (!cfg?.cards?.length) return null;
  const chosen =
    chosenPresetId != null && chosenPresetId !== ''
      ? cfg.presets.find(p => String(p.id) === String(chosenPresetId)) || null
      : null;
  const suggested = matchPresetFromPicks(cfg, picks, { chosenPresetId });
  const preset = preferChosenWhenSameScopes(cfg, chosen, suggested, picks) || suggested || chosen || resolvePreset(cfg, picks);
  if (!preset) return null;
  const card = cardById(cfg, preset.cardId);
  if (!card) return null;
  const exact = exactMatch(cfg, preset, picks);
  const why = whyOnCard(preset, picks);
  const colIdx = rangeIndexForTrade(cfg, typicalStdIdx);
  const days = daysAt(card, colIdx, { iso, side: 'customer' });
  return {
    cfg,
    preset,
    card,
    exact,
    why,
    colIdx,
    days,
    suggested: !!suggested
  };
};
